(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let pendingOriginRect = null;

  function quadBezier(t, p0, p1, p2) {
    const u = 1 - t;
    return u * u * p0 + 2 * u * t * p1 + t * t * p2;
  }

  function captureOriginRect(el) {
    if (!el) return null;
    const icon = el.querySelector('[class*="__icon"]') || el;
    return icon.getBoundingClientRect();
  }

  function setAddOrigin(el) {
    pendingOriginRect = captureOriginRect(el) || el.getBoundingClientRect();
  }

  function consumeOriginRect() {
    const rect = pendingOriginRect;
    pendingOriginRect = null;
    return rect;
  }

  function flyParticle(fromRect, toRect) {
    return new Promise((resolve) => {
      const particle = document.createElement('span');
      particle.className = 'archive-particle';
      particle.setAttribute('aria-hidden', 'true');
      document.body.appendChild(particle);

      const sx = fromRect.left + fromRect.width / 2;
      const sy = fromRect.top + fromRect.height / 2;
      const ex = toRect.left + toRect.width / 2;
      const ey = toRect.top + toRect.height / 2;
      const cx = (sx + ex) / 2;
      const cy = Math.min(sy, ey) - Math.max(72, Math.abs(ex - sx) * 0.2 + 48);

      particle.style.left = `${sx}px`;
      particle.style.top = `${sy}px`;

      const duration = 640;
      const start = performance.now();

      function frame(now) {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 2.6);
        const x = quadBezier(eased, sx, cx, ex);
        const y = quadBezier(eased, sy, cy, ey);
        const scale = 1 - t * 0.4;
        particle.style.transform = `translate(${x - sx}px, ${y - sy}px) scale(${scale})`;
        particle.style.opacity = t > 0.82 ? String(1 - (t - 0.82) / 0.18) : '1';

        if (t < 1) requestAnimationFrame(frame);
        else {
          particle.remove();
          resolve();
        }
      }

      requestAnimationFrame(frame);
    });
  }

  function updateCountBadge(badge, count, { bump = false } = {}) {
    if (!badge) return;
    const n = Number(count);
    const prev = Number(badge.querySelector('[data-count-value]')?.textContent || badge.textContent) || 0;

    if (n > 0) {
      const valueEl = badge.querySelector('[data-count-value]') || badge;
      valueEl.textContent = n > 99 ? '99+' : String(n);
      badge.hidden = false;
      badge.removeAttribute('aria-hidden');
      badge.classList.add('is-visible');

      if (bump || (prev > 0 && n > prev)) {
        badge.classList.remove('is-bump');
        void badge.offsetWidth;
        badge.classList.add('is-bump');
        badge.addEventListener('animationend', () => badge.classList.remove('is-bump'), { once: true });
      }
      return;
    }

    badge.classList.remove('is-bump', 'is-visible');
    badge.setAttribute('aria-hidden', 'true');
    const hideMs = reducedMotion ? 0 : 320;
    window.setTimeout(() => {
      if (!badge.classList.contains('is-visible')) badge.hidden = true;
    }, hideMs);
  }

  function getCountBadge(link) {
    return link?.querySelector('.wishlist-count-bubble, .cart-count-bubble');
  }

  function syncWishlistHeaderFilled(link, count) {
    if (!link?.classList.contains('header__icon--wishlist')) return;
    const n = Math.max(0, Number(count) || 0);
    link.classList.toggle('has-items', n > 0);
    link.setAttribute('aria-label', n > 0 ? `Wishlist, ${n} item${n === 1 ? '' : 's'}` : 'Wishlist');
  }

  function playIconLanding(link, count) {
    const badge = getCountBadge(link);
    const n = Math.max(0, Number(count) || 0);

    if (!link || reducedMotion) {
      updateCountBadge(badge, n, { bump: true });
      syncWishlistHeaderFilled(link, n);
      return;
    }

    window.clearTimeout(playIconLanding._fade);
    window.clearTimeout(playIconLanding._end);

    syncWishlistHeaderFilled(link, n);
    link.classList.remove('is-accent-fading', 'is-icon-bounce', 'is-accent');
    void link.offsetWidth;
    link.classList.add('is-accent', 'is-icon-bounce');

    link.addEventListener(
      'animationend',
      (e) => {
        if (e.animationName === 'archive-header-icon-bounce') {
          link.classList.remove('is-icon-bounce');
        }
      },
      { once: true },
    );

    window.setTimeout(() => updateCountBadge(badge, n, { bump: true }), 90);

    playIconLanding._fade = window.setTimeout(() => {
      link.classList.add('is-accent-fading');
    }, 950);

    playIconLanding._end = window.setTimeout(() => {
      link.classList.remove('is-accent', 'is-accent-fading', 'is-icon-bounce');
      syncWishlistHeaderFilled(link, n);
    }, 1450);
  }

  async function celebrate({ originRect, originEl, link, targetSelector, count }) {
    let fromRect = originRect;
    if (!fromRect && originEl) {
      const icon = originEl.querySelector('[class*="__icon"]') || originEl;
      fromRect = icon.getBoundingClientRect();
    }

    const target = link?.querySelector(targetSelector) || link;

    if (!link || !fromRect || !target || reducedMotion) {
      playIconLanding(link, count);
      return;
    }

    await flyParticle(fromRect, target.getBoundingClientRect());
    playIconLanding(link, count);
  }

  function afterCartSectionPaint(fn) {
    requestAnimationFrame(() => requestAnimationFrame(fn));
  }

  function runCartCelebration(parsedState) {
    const originRect = consumeOriginRect();
    if (!originRect) return;

    const count = parsedState?.item_count;
    if (count == null) return;

    afterCartSectionPaint(() => {
      const link = document.querySelector('#cart-icon-bubble');
      if (!link) return;

      celebrate({
        originRect,
        link,
        targetSelector: '.header__cart-icon--outline',
        count,
      });
    });
  }

  function patchCartRenderContents() {
    const patch = (Ctor) => {
      if (!Ctor?.prototype?.renderContents || Ctor.prototype._archiveCelebratePatched) return;
      const original = Ctor.prototype.renderContents;
      Ctor.prototype.renderContents = function (parsedState) {
        original.call(this, parsedState);
        runCartCelebration(parsedState);
      };
      Ctor.prototype._archiveCelebratePatched = true;
    };

    patch(customElements.get('cart-notification'));
    patch(customElements.get('cart-drawer'));
  }

  function syncInitialCartBadge() {
    const badge = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    const value = badge?.querySelector('[data-count-value]');
    const n = Number(value?.textContent);
    if (badge && n > 0) {
      badge.hidden = false;
      badge.removeAttribute('aria-hidden');
      badge.classList.add('is-visible');
    }
  }

  function initCartCelebration() {
    const addOriginSelector =
      'product-form [type="submit"], .quick-add__submit, [data-quick-add] button, quick-add-modal [type="submit"]';

    document.addEventListener(
      'pointerdown',
      (e) => {
        const el = e.target.closest(addOriginSelector);
        if (el) setAddOrigin(el);
      },
      true,
    );

    patchCartRenderContents();
    customElements.whenDefined('cart-notification').then(patchCartRenderContents);
    customElements.whenDefined('cart-drawer').then(patchCartRenderContents);
    syncInitialCartBadge();
  }

  window.archiveCelebrate = {
    reducedMotion,
    setAddOrigin,
    consumeOriginRect,
    flyParticle,
    playIconLanding,
    updateCountBadge,
    celebrate,
    afterCartSectionPaint,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartCelebration);
  } else {
    initCartCelebration();
  }
})();
